<template>
  <section
    :class="[
      props.frameless
        ? 'bg-transparent overflow-hidden'
        : 'bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'
    ]"
  >
    <div :class="props.frameless ? 'pt-1 pb-2' : 'px-6 pt-5 pb-4 border-b border-gray-200'">
      <div>
        <h2 class="mb-0 text-xl font-semibold leading-tight text-black">
          Quantité de produits vendus (pièces)
        </h2>
        <p class="mb-0 mt-2 text-sm leading-tight text-gray-500">{{ periodLabel }}</p>
      </div>
    </div>

    <div :class="props.frameless ? 'pt-3 pb-5' : 'px-6 py-5'">
      <div class="grid grid-cols-[minmax(0,1fr)_72px_92px] items-end gap-3 border-b-2 border-gray-400 pb-2 text-sm sm:grid-cols-[minmax(0,1fr)_96px_112px] sm:gap-4">
        <div class="font-medium text-black">Produits</div>
        <div class="text-right">
          <div class="font-medium text-black">Qté (pcs)</div>
          <div class="text-xs text-black">{{ totalQuantity }}</div>
        </div>
        <div class="text-right">
          <div class="font-medium text-black">Montant (HT)</div>
          <div class="text-xs text-black">{{ formatCurrency(totalAmount) }}</div>
        </div>
      </div>

      <div v-if="loading" class="space-y-4 py-6">
        <div v-for="index in 5" :key="index" class="animate-pulse">
          <div class="grid grid-cols-[minmax(0,1fr)_72px_92px] items-center gap-3 sm:grid-cols-[minmax(0,1fr)_96px_112px] sm:gap-4">
            <div class="flex items-center gap-3">
              <div class="h-9 w-9 rounded bg-gray-200"></div>
              <div class="h-3 w-2/3 rounded bg-gray-200"></div>
            </div>
            <div class="ml-auto h-3 w-10 rounded bg-gray-200"></div>
            <div class="ml-auto h-3 w-16 rounded bg-gray-200"></div>
          </div>
          <div class="mt-3 h-1 rounded bg-gray-100"></div>
        </div>
      </div>

      <div v-else-if="rows.length === 0" class="py-10 text-center text-sm text-gray-500">
        Aucune vente sur cette période.
      </div>

      <div v-else class="divide-y divide-gray-100">
        <article v-for="row in rows" :key="row.id" class="py-4">
          <div class="grid grid-cols-[minmax(0,1fr)_72px_92px] items-center gap-3 sm:grid-cols-[minmax(0,1fr)_96px_112px] sm:gap-4">
            <div class="min-w-0 flex items-center gap-3">
              <div class="h-9 w-9 shrink-0 overflow-hidden rounded bg-gray-100 ring-1 ring-gray-200">
                <img
                  v-if="row.imageUrl && !failedImages[row.id]"
                  :src="row.imageUrl"
                  :alt="row.reference"
                  class="h-full w-full object-cover"
                  @error="failedImages[row.id] = true"
                />
                <div v-else class="flex h-full w-full items-center justify-center text-xs font-semibold text-gray-500">
                  {{ getInitial(row.reference) }}
                </div>
              </div>
              <div class="min-w-0">
                <div class="truncate text-sm font-medium text-black">{{ row.reference }}</div>
                <div v-if="row.categorie" class="truncate text-xs text-gray-500">{{ row.categorie }}</div>
              </div>
            </div>

            <div class="text-right text-sm font-medium text-black">{{ row.ventes }}</div>
            <div class="text-right text-sm font-medium text-black">{{ row.chiffreAffaires }}</div>
          </div>

          <div class="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div
              class="h-full rounded-full bg-blue-700"
              :style="{ width: `${getWidth(row.ventes)}%` }"
            ></div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'
import type { ProduitRow } from '@/types'

const props = withDefaults(defineProps<{
  rows: ProduitRow[]
  loading?: boolean
  periodLabel: string
  frameless?: boolean
}>(), {
  rows: () => [],
  loading: false,
  frameless: false,
})

const failedImages = reactive<Record<number, boolean>>({})

const totalQuantity = computed(() =>
  props.rows.reduce((total, row) => total + Number(row.ventes || 0), 0),
)

const totalAmount = computed(() =>
  props.rows.reduce((total, row) => {
    if (typeof row.chiffreAffaires === 'number') {
      return total + row.chiffreAffaires
    }
    const normalized = String(row.chiffreAffaires || '0')
      .replace(/\s/g, '')
      .replace('€', '')
      .replace(',', '.')
    return total + (Number.parseFloat(normalized) || 0)
  }, 0),
)

const maxQuantity = computed(() =>
  Math.max(1, ...props.rows.map((row) => Number(row.ventes || 0))),
)

const formatCurrency = (value: number) =>
  `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

const getWidth = (quantity: number) =>
  Math.max(3, Math.round((Number(quantity || 0) / maxQuantity.value) * 100))

const getInitial = (reference: string) =>
  (reference || '?').trim().charAt(0).toUpperCase() || '?'
</script>
